import { errorMessage } from "@atlas/shared";
import React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronDown, ImagePlus, MountainSnow, Paperclip, X } from "lucide-react";
import { createPost, getPhotoUploadUrl, patchAccount, type PostKind } from "../api/social";
import { fetchAchievements, type Achievement } from "../api/achievements";
import { fetchActivities, type Activity } from "../api/activities";
import { AtlasPanel } from "./AtlasPanel";
import { Button } from "./Button";
import { iconByName as achievementIcon } from "../lib/achievement-icons";

// Post composer + username claim prompt, shared by the community feed
// (pages/social/FeedPage.tsx) and the group page (spec 32). With `groupId`
// the audience is fixed to that group: the post goes to POST /social/posts
// with { visibility: "group", groupId } and only members see it.

const USERNAME_RE = /^[a-z0-9-]{3,24}$/;
const ALLOWED_PHOTO_TYPES = ["image/jpeg", "image/png", "image/webp"];
const MAX_PHOTOS = 4;
const MAX_PHOTO_BYTES = 5 * 1024 * 1024;
const MAX_BODY = 2000;

// ─── Username claim (posting needs a username; the dashboard has its own
// banner component) ────────────────────────────────────────────────────────────

export function UsernameClaimPrompt() {
  const queryClient = useQueryClient();
  const [value, setValue] = React.useState("");
  const normalized = value.trim().toLowerCase();
  const valid = USERNAME_RE.test(normalized);

  const claimMutation = useMutation({
    mutationFn: (username: string) => patchAccount({ username }),
    onSuccess: async () => {
      setValue("");
      await queryClient.invalidateQueries({ queryKey: ["me"] });
    },
  });

  return (
    <AtlasPanel>
      <p className="eyebrow" style={{ fontSize: 10, marginBottom: 6 }}>Community</p>
      <h3 style={{ font: "var(--type-h3)", color: "var(--text-primary)", margin: "0 0 6px" }}>
        Claim your username
      </h3>
      <p style={{ fontSize: 13.5, color: "var(--text-secondary)", lineHeight: 1.5, margin: "0 0 14px" }}>
        Pick a username to post, comment and give awards. It also becomes your public profile link.
      </p>
      <div style={{ display: "flex", gap: 10, maxWidth: 380 }}>
        <input
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="yourname"
          maxLength={24}
          style={{
            flex: 1,
            height: "var(--control-md)",
            padding: "0 12px",
            background: "var(--surface-sunken)",
            border: "1px solid var(--border-default)",
            borderRadius: "var(--radius-md)",
            color: "var(--text-primary)",
            fontSize: 14,
            fontFamily: "var(--font-mono)",
            outline: "none",
          }}
        />
        <Button
          disabled={!valid || claimMutation.isPending}
          onClick={() => claimMutation.mutate(normalized)}
        >
          {claimMutation.isPending ? "Claiming…" : "Claim"}
        </Button>
      </div>
      {value.length > 0 && !valid && (
        <p style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 8, marginBottom: 0 }}>
          3–24 characters: lowercase letters, numbers and hyphens only.
        </p>
      )}
      {claimMutation.isError && (
        <p style={{ fontSize: 12, color: "var(--status-danger)", marginTop: 8, marginBottom: 0 }}>
          {errorMessage(claimMutation.error, "Failed to claim username")}
        </p>
      )}
    </AtlasPanel>
  );
}

// ─── Composer photo state ───────────────────────────────────────────────────────

interface ComposerPhoto {
  localId: string;
  previewUrl: string;
  width: number | null;
  height: number | null;
  key: string | null;
  uploading: boolean;
  error: string | null;
}

function readImageDimensions(url: string): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
    img.onerror = () => reject(new Error("Could not read image"));
    img.src = url;
  });
}

// ─── Composer ───────────────────────────────────────────────────────────────────

export function Composer({
  attach,
  groupId,
  placeholder = "Share an adventure with the community…",
}: {
  attach: { kind: PostKind; id: string } | null;
  /** Group audience (spec 32): fixed, not a choice in the composer. */
  groupId?: string;
  placeholder?: string;
}) {
  const queryClient = useQueryClient();
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const appliedAttachRef = React.useRef(false);

  const [body, setBody] = React.useState("");
  const [kind, setKind] = React.useState<PostKind>("text");
  const [achievementId, setAchievementId] = React.useState<string | null>(null);
  const [activityId, setActivityId] = React.useState<string | null>(null);
  const [photos, setPhotos] = React.useState<ComposerPhoto[]>([]);
  const [attachMenuOpen, setAttachMenuOpen] = React.useState(false);

  const achievementsQuery = useQuery({ queryKey: ["achievements"], queryFn: fetchAchievements });
  const activitiesQuery = useQuery({ queryKey: ["activities", "recent-attach"], queryFn: () => fetchActivities(20, 0) });

  React.useEffect(() => {
    if (appliedAttachRef.current || !attach) return;
    appliedAttachRef.current = true;
    if (attach.kind === "achievement") {
      setKind("achievement");
      setAchievementId(attach.id);
    } else if (attach.kind === "activity") {
      setKind("activity");
      setActivityId(attach.id);
    }
  }, [attach]);

  const unlockedAchievements = React.useMemo(
    () =>
      (achievementsQuery.data?.achievements ?? [])
        .filter((a) => a.unlocked)
        .sort((a, b) => (b.unlockedAt ?? "").localeCompare(a.unlockedAt ?? ""))
        .slice(0, 5),
    [achievementsQuery.data],
  );
  const recentActivities = (activitiesQuery.data?.activities ?? []).slice(0, 5);

  const selectedAchievement: Achievement | undefined = achievementId
    ? (achievementsQuery.data?.achievements ?? []).find((a) => a.id === achievementId)
    : undefined;
  const selectedActivity: Activity | undefined = activityId
    ? (activitiesQuery.data?.activities ?? []).find((a) => a.id === activityId)
    : undefined;

  function clearAttachment() {
    setKind("text");
    setAchievementId(null);
    setActivityId(null);
  }

  function selectAchievement(a: Achievement) {
    setKind("achievement");
    setAchievementId(a.id);
    setActivityId(null);
    setAttachMenuOpen(false);
  }

  function selectActivity(a: Activity) {
    setKind("activity");
    setActivityId(a.id);
    setAchievementId(null);
    setAttachMenuOpen(false);
  }

  async function handleFiles(fileList: FileList) {
    const remaining = MAX_PHOTOS - photos.length;
    const files = Array.from(fileList).slice(0, Math.max(0, remaining));
    for (const file of files) {
      if (!ALLOWED_PHOTO_TYPES.includes(file.type)) {
        window.alert("Photos must be JPEG, PNG or WebP");
        continue;
      }
      if (file.size > MAX_PHOTO_BYTES) {
        window.alert(`${file.name} is over 5 MB`);
        continue;
      }

      const localId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const previewUrl = URL.createObjectURL(file);
      setPhotos((prev) => [
        ...prev,
        { localId, previewUrl, width: null, height: null, key: null, uploading: true, error: null },
      ]);

      try {
        const [{ width, height }, { uploadUrl, key }] = await Promise.all([
          readImageDimensions(previewUrl),
          getPhotoUploadUrl(file.type),
        ]);
        const uploadRes = await fetch(uploadUrl, {
          method: "PUT",
          body: file,
          headers: { "Content-Type": file.type },
        });
        if (!uploadRes.ok) throw new Error("Upload failed");
        setPhotos((prev) =>
          prev.map((p) => (p.localId === localId ? { ...p, width, height, key, uploading: false } : p)),
        );
      } catch (err) {
        setPhotos((prev) =>
          prev.map((p) =>
            p.localId === localId
              ? { ...p, uploading: false, error: errorMessage(err, "Upload failed") }
              : p,
          ),
        );
      }
    }
  }

  function removePhoto(localId: string) {
    setPhotos((prev) => {
      const target = prev.find((p) => p.localId === localId);
      if (target) URL.revokeObjectURL(target.previewUrl);
      return prev.filter((p) => p.localId !== localId);
    });
  }

  // Idempotency key for POST /social/posts (clientKey): one per submit of
  // this content, so a double click or a re-click after a timeout that may
  // have landed returns the first post instead of posting twice. Dropped on
  // reset; a change to the content gets a new one.
  const clientKeyRef = React.useRef<{ sig: string; key: string } | null>(null);
  function clientKeyFor(content: unknown): string {
    const sig = JSON.stringify(content);
    if (clientKeyRef.current?.sig !== sig) clientKeyRef.current = { sig, key: crypto.randomUUID() };
    return clientKeyRef.current.key;
  }

  function resetComposer() {
    clientKeyRef.current = null;
    setBody("");
    clearAttachment();
    setPhotos((prev) => {
      prev.forEach((p) => URL.revokeObjectURL(p.previewUrl));
      return [];
    });
  }

  const createMutation = useMutation({
    mutationFn: createPost,
    onSuccess: async () => {
      resetComposer();
      await queryClient.invalidateQueries({ queryKey: ["social/feed"] });
    },
  });

  const trimmedBody = body.trim();
  const readyPhotos = photos.filter((p) => p.key && !p.uploading && !p.error);
  const anyUploading = photos.some((p) => p.uploading);
  const hasTextContent = kind !== "text" || trimmedBody.length > 0 || readyPhotos.length > 0;
  const canPost =
    hasTextContent && trimmedBody.length <= MAX_BODY && !anyUploading && !createMutation.isPending;

  function handlePost() {
    if (!canPost) return;
    const content = {
      kind,
      body: trimmedBody || undefined,
      achievementId: kind === "achievement" ? achievementId ?? undefined : undefined,
      activityId: kind === "activity" ? activityId ?? undefined : undefined,
      photos: readyPhotos.length
        ? readyPhotos.map((p) => ({ key: p.key!, width: p.width ?? undefined, height: p.height ?? undefined }))
        : undefined,
      ...(groupId ? { visibility: "group" as const, groupId } : {}),
    };
    createMutation.mutate({ ...content, clientKey: clientKeyFor(content) });
  }

  return (
    <AtlasPanel>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder={placeholder}
          rows={3}
          style={{
            width: "100%",
            resize: "vertical",
            padding: 12,
            borderRadius: "var(--radius-md)",
            border: "1px solid var(--border-default)",
            background: "var(--surface-sunken)",
            color: "var(--text-primary)",
            fontSize: 14,
            fontFamily: "var(--font-sans)",
            outline: "none",
            boxSizing: "border-box",
          }}
        />
        {body.length > 1800 && (
          <span
            className="tnum"
            style={{
              alignSelf: "flex-end",
              marginTop: -8,
              fontSize: 11,
              fontFamily: "var(--font-mono)",
              color: body.length > MAX_BODY ? "var(--status-danger)" : "var(--text-muted)",
            }}
          >
            {body.length}/{MAX_BODY}
          </span>
        )}

        {/* Attachment preview */}
        {kind === "achievement" && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              padding: 12,
              borderRadius: "var(--radius-md)",
              background: "var(--accent-soft)",
              border: "1px solid var(--border-gold)",
            }}
          >
            <span style={{ color: "var(--gold-400)" }}>
              {achievementIcon(selectedAchievement?.iconName ?? "award", 20)}
            </span>
            <div style={{ flex: 1, minWidth: 0, fontSize: 13.5, color: "var(--text-primary)", fontWeight: 600 }}>
              {selectedAchievement?.name ?? "Achievement"}
            </div>
            <button
              type="button"
              onClick={clearAttachment}
              aria-label="Remove attachment"
              style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)" }}
            >
              <X size={14} />
            </button>
          </div>
        )}
        {kind === "activity" && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              padding: 12,
              borderRadius: "var(--radius-md)",
              background: "var(--surface-sunken)",
              border: "1px solid var(--border-subtle)",
            }}
          >
            <MountainSnow size={20} style={{ color: "var(--sky-400)" }} />
            <div style={{ flex: 1, minWidth: 0, fontSize: 13.5, color: "var(--text-primary)", fontWeight: 600 }}>
              {selectedActivity?.name ?? "Activity"}
            </div>
            <button
              type="button"
              onClick={clearAttachment}
              aria-label="Remove attachment"
              style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)" }}
            >
              <X size={14} />
            </button>
          </div>
        )}

        {/* Photo thumbnails */}
        {photos.length > 0 && (
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {photos.map((p) => (
              <div key={p.localId} style={{ position: "relative", width: 72, height: 72 }}>
                <img
                  src={p.previewUrl}
                  alt=""
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    borderRadius: "var(--radius-sm)",
                    opacity: p.uploading ? 0.5 : 1,
                    border: p.error ? "1px solid var(--status-danger)" : "1px solid var(--border-subtle)",
                  }}
                />
                <button
                  type="button"
                  onClick={() => removePhoto(p.localId)}
                  aria-label="Remove photo"
                  style={{
                    position: "absolute",
                    top: -6,
                    right: -6,
                    width: 20,
                    height: 20,
                    borderRadius: "50%",
                    background: "var(--surface-card)",
                    border: "1px solid var(--border-default)",
                    display: "grid",
                    placeItems: "center",
                    cursor: "pointer",
                    color: "var(--text-secondary)",
                  }}
                >
                  <X size={11} />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Toolbar */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, position: "relative" }}>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              multiple
              style={{ display: "none" }}
              onChange={(e) => {
                if (e.target.files && e.target.files.length > 0) void handleFiles(e.target.files);
                e.target.value = "";
              }}
            />
            <Button
              variant="ghost"
              size="sm"
              leftIcon={<ImagePlus size={14} />}
              disabled={photos.length >= MAX_PHOTOS}
              onClick={() => fileInputRef.current?.click()}
            >
              Photo
            </Button>
            <Button
              variant="ghost"
              size="sm"
              leftIcon={<Paperclip size={14} />}
              rightIcon={<ChevronDown size={12} />}
              onClick={() => setAttachMenuOpen((v) => !v)}
            >
              Attach
            </Button>
            {attachMenuOpen && (
              <div
                className="atlas-glass"
                style={{
                  position: "absolute",
                  top: "calc(100% + 6px)",
                  left: 0,
                  zIndex: 30,
                  width: 280,
                  maxHeight: 320,
                  overflow: "auto",
                  borderRadius: "var(--radius-lg)",
                  boxShadow: "var(--shadow-lg)",
                  padding: 10,
                }}
              >
                <p className="eyebrow" style={{ fontSize: 9.5, margin: "4px 8px" }}>Recent unlocks</p>
                {unlockedAchievements.length === 0 ? (
                  <p style={{ fontSize: 12, color: "var(--text-faint)", margin: "0 8px 8px" }}>None yet</p>
                ) : (
                  unlockedAchievements.map((a) => (
                    <button
                      key={a.id}
                      type="button"
                      onClick={() => selectAchievement(a)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        width: "100%",
                        padding: "7px 8px",
                        border: "none",
                        background: "transparent",
                        cursor: "pointer",
                        borderRadius: "var(--radius-sm)",
                        textAlign: "left",
                        color: "var(--text-primary)",
                        fontSize: 13,
                      }}
                    >
                      <span style={{ color: "var(--gold-400)", flexShrink: 0 }}>{achievementIcon(a.iconName, 15)}</span>
                      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.name}</span>
                    </button>
                  ))
                )}

                <div style={{ height: 1, background: "var(--border-subtle)", margin: "8px 4px" }} />

                <p className="eyebrow" style={{ fontSize: 9.5, margin: "4px 8px" }}>Recent activities</p>
                {recentActivities.length === 0 ? (
                  <p style={{ fontSize: 12, color: "var(--text-faint)", margin: "0 8px 8px" }}>None yet</p>
                ) : (
                  recentActivities.map((act) => (
                    <button
                      key={act.id}
                      type="button"
                      onClick={() => selectActivity(act)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        width: "100%",
                        padding: "7px 8px",
                        border: "none",
                        background: "transparent",
                        cursor: "pointer",
                        borderRadius: "var(--radius-sm)",
                        textAlign: "left",
                        color: "var(--text-primary)",
                        fontSize: 13,
                      }}
                    >
                      <MountainSnow size={15} style={{ color: "var(--sky-400)", flexShrink: 0 }} />
                      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{act.name}</span>
                    </button>
                  ))
                )}
              </div>
            )}
          </div>

          <Button disabled={!canPost} onClick={handlePost}>
            {createMutation.isPending ? "Posting…" : "Post"}
          </Button>
        </div>

        {createMutation.isError && (
          <p style={{ fontSize: 12.5, color: "var(--status-danger)", margin: 0 }}>
            {errorMessage(createMutation.error, "Failed to post")}
          </p>
        )}
      </div>
    </AtlasPanel>
  );
}

